import React, { ChangeEvent, useEffect, useState } from 'react'
import ButtonIcon from '../../components/ButtonComp'
import { faArrowsRotate, faDatabase, faPaperPlane, faPlus, faSync } from '@fortawesome/free-solid-svg-icons'
import PreviewComp from './Preview'
import AspectComp from './Aspect'
import { IAspectStore, IPMVStatus } from '../../models/Pmv'
import { getPMVApi, handleResetApi, sendPostRequestApi, storePmv } from '../../api/its/pmv'
import useConfirm from '../../hooks/useConfirm'
import { Alert, Space, TreeProps } from 'antd'
import { IAsset } from '../../models/Assets'
import { useNotificationContext } from '../../services/NotificationContext'
import PMVHooks from './PMVHooks'
import { useMutation } from '@apollo/client'
import { INSERT_PMV_STATUS } from '../../graphql/mutations/pmv_status'
import usePMV from 'hooks/usePMV'

export interface FormState {
    messages: string[];
    pictogram: string;
    textSize: number;
    textColor: string;
    textAlign: "center" | "left" | "right";
}

type Props = {
    assetId: number;
    checkedKeys: TreeProps['checkedKeys'];
    assetsPMV: IAsset[];
    hookPMV: ReturnType<typeof usePMV>;
}

const AspectsComp: React.FC<Props> = ({ assetId, checkedKeys, assetsPMV, hookPMV }: Props) => {
    const initialAspect: FormState = {
        messages: ["", "", ""], // Inicializar con tres mensajes vacíos
        pictogram: "none",
        textSize: 30,
        textColor: "#ffffff",
        textAlign: "center",
    };
    const { show } = useNotificationContext();
    const { showPromiseConfirm } = useConfirm();
    const [formState, setFormState] = useState<FormState[]>([initialAspect]);
    const [currentAspectIndex, setCurrentAspectIndex] = useState(0);
    const { updatePanelState, hookPMVStatuses, processAspects, processAspectsObject } = PMVHooks();

    const [insertPmvStatus] = useMutation(INSERT_PMV_STATUS);

    // Si desde la sección de "Almacenados" se selecciona un panel, se cargan los aspectos del panel seleccionado
    useEffect(() => {
        if (hookPMV.selectedItem) {
            const selectedItem = hookPMV.selectedItem;
            const currentState: FormState[] = processAspectsObject(selectedItem.aspects);
            setFormState(currentState);
        }
    }, [hookPMV.selectedItem]);
    // Obtener el mensaje del panel actual, en caso de que no haya seleccionado uno desde "Almacenados"
    useEffect(() => {
        // Si hay un panel seleccionado en "Almacenados", no se ejecuta el efecto
        if (hookPMV.selectedItem) return;
        // Si el estado de carga es verdadero, no se ejecuta el efecto
        if (hookPMVStatuses.loading) return;
        // Obtener el estado del panel actual
        const currentPanel = hookPMVStatuses.data?.its_pmv_statuses.find((panel: IPMVStatus) => panel.pmv_id === assetId);
        const currentState: FormState[] = processAspects(currentPanel?.message || "");
        setFormState(currentState);
    }, [hookPMVStatuses.loading]);
    // Manejo de alternancia automática de aspectos
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentAspectIndex((prevIndex) => (prevIndex + 1) % formState.length);
        }, 2000); // Cambiar cada 2 segundos
        return () => clearInterval(interval); // Limpiar el intervalo al desmontar el componente
    }, [formState.length]);

    // Manejar cambio de mensajes
    const handleMessageChange = (aspectIndex: number, messageIndex: number, e: ChangeEvent<HTMLTextAreaElement>) => {
        const newFormState = [...formState];
        newFormState[aspectIndex].messages[messageIndex] = e.target.value;
        setFormState(newFormState);
    };
    // Manejar el cambio de pictograma
    const handlePictogramChange = (index: number, pictogram: string) => {
        const newFormState = [...formState];
        newFormState[index].pictogram = pictogram;
        setFormState(newFormState);
    };
    // Manejar el cambio de aspecto
    const handleAddAspect = () => {
        if (formState.length >= 3) {
            alert("Sólo se pueden agregar 3 aspectos");
        } else {
            setFormState([...formState, initialAspect]);
        }
    };
    // Manejar el cambio de alineación del texto
    const handleTextAlignChange = (index: number, value: "center" | "left" | "right") => {
        const newFormState = [...formState];
        newFormState[index].textAlign = value;
        setFormState(newFormState);
    };
    // Mandar mensaje al panel: Enviar la configuración a los paneles seleccionados
    const handleSendMessage = async () => {
        const content = (
            <>
                <p>¿Está seguro que desea actualizar estos paneles?</p>
                <ul>
                    {Array.isArray(checkedKeys) && checkedKeys.map(panel_id => {
                        const panel = assetsPMV.find(asset => asset.id === panel_id);
                        return <li key={panel_id}>{panel?.description}</li>
                    })}
                </ul>
            </>
        );

        showPromiseConfirm({
            title: 'Actualizar panel(es)',
            content,
            okText: 'Actualizar',
            actionCallback: async () => {
                if (Array.isArray(checkedKeys))
                    for (const panel of assetsPMV.filter(asset => checkedKeys.includes(asset.id))) {
                        const configuration = formState.map((aspect) => {
                            const lines = aspect.messages.map((message, i) =>
                                message.trim()
                                    ? `[tr65,${i * 16 + 1},0,0]${message.toUpperCase().padEnd(18, " ").slice(0, 18)}`
                                    : `[tr65,${i * 16 + 1},0,0]`
                            );
                            // Extraer el número del pictograma si existe
                            const regex = /\/static\/media\/(\d+)\./;
                            const match = aspect.pictogram.match(regex);
                            const pictogramNumber = match ? match[1] : null;

                            const pictogramTag = pictogramNumber ? `[g${pictogramNumber}]` : "";
                            const aspectTagStart = formState.length > 1 ? "[pt10o0]" : "";
                            const aspectTagEnd = formState.length > 1 ? "[np]" : "";

                            return [aspectTagStart, pictogramTag, ...lines, aspectTagEnd].filter(Boolean).join("");
                        });
                        const response = await sendPostRequestApi(configuration.join(""), panel.id);
                        if (response.status == 200) {
                            if (response.message.trim() === configuration.join("").trim()) {
                                // Insertar registro en la base de datos
                                const responseDB = await insertPmvStatus({ variables: { message: configuration.join(""), pmv_id: panel.id } });
                                console.log("🚀 ~ actionCallback: ~ responseDB:", responseDB)
                                // Actualizar el estado del panel
                                updatePanelState(panel.id, configuration.join(""), true);
                                show("success", "Panel '" + panel.description + "' actualizado", "La configuración del panel ha sido actualizada correctamente.");
                            } else {
                                show("warning", "Panel '" + panel.description + "' se ha intentado actualizar", "Es posible que la configuración del panel no haya sido actualizada correctamente.");
                            }
                        } else {
                            show("error", "Error al actualizar el panel", "Es posible que el panel '" + panel.description + "' no haya podido ser actualizado.");
                        }
                    }
            }
        });
    }
    // Guardar configuración en base de datos
    const handleStoreMessage = async () => {
        showPromiseConfirm({
            title: 'Guardar configuración',
            content: '¿Está seguro que desea guardar la configuración?',
            okText: 'Guardar',
            actionCallback: async () => {
                let description = '';
                let aspects: IAspectStore[] = [];
                for (const aspect of formState) {
                    const { pictogram, messages, textAlign } = aspect;
                    description += `${messages[0].toUpperCase()}|${messages[1].toUpperCase()}|${messages[2].toUpperCase()}`
                    aspects.push({ pictogram, line1: messages[0].toUpperCase(), line2: messages[1].toUpperCase(), line3: messages[2].toUpperCase(), textAlign });
                }
                const response = await storePmv({ description, package_id: 17, aspects });
                if (response.status === 201) {
                    show("success", "Configuración guardada", "La configuración ha sido guardada correctamente.");
                    hookPMV.addElement(response.data);
                } else {
                    show("error", "Error al guardar la configuración", "Es posible que la configuración no haya podido ser guardada.");
                }
            }
        });
    }
    // Resetear configuración del panel: Resetear la configuración de los paneles seleccionados
    const handleReset = async () => {
        const content = (
            <>
                <p>¿Está seguro que desea resetear estos paneles?</p>
                <ul>
                    {Array.isArray(checkedKeys) && checkedKeys.map(panel_id => {
                        const panel = assetsPMV.find(asset => asset.id === panel_id);
                        return <li key={panel_id}>{panel?.description}</li>
                    })}
                </ul>
            </>
        );

        showPromiseConfirm({
            title: 'Resetear panel(es)',
            content,
            okText: 'Resetear',
            actionCallback: async () => {
                if (Array.isArray(checkedKeys))
                    for (const panel of assetsPMV.filter(asset => checkedKeys.includes(asset.id))) {
                        try {
                            const response = await handleResetApi(panel.id);
                            if (response === 200) {
                                show("success", "Panel '" + panel.description + "' se ha reseteado correctamente", "La configuración del panel ha sido restablecida a la predeterminada.");
                                const responseDB = await insertPmvStatus({ variables: { message: "", pmv_id: panel.id } });
                                setFormState([initialAspect]);
                            } else {
                                show("info", "Validando estado del panel '" + panel.description, "Esperando respuesta del panel...");
                                setTimeout(async () => {
                                    const currentState = await getPMVApi(panel.id);
                                    if (currentState.success == true && currentState.response.trim() === "") {
                                        const responseDB = await insertPmvStatus({ variables: { message: "", pmv_id: panel.id } });
                                        show("success", "Panel '" + panel.description + "' se ha reseteado correctamente", "La configuración del panel ha sido restablecida a la predeterminada.");
                                        setFormState([initialAspect]);
                                    } else {
                                        show("warning", "Panel '" + panel.description + "' se ha intentado resetear", "Es posible que la configuración del panel no haya sido restablecida a la predeterminada.");
                                    }

                                }, 3000);
                            }

                        } catch (error) {
                            show("warning", "Error al resetear el panel", "Es posible que el panel '" + panel.description + "' no haya podido ser reseteado.");
                        }
                    }
            }
        });
    }

    return (
        <div>
            {
                hookPMVStatuses.loading && <Alert type='info' message='Obteniendo aspecto actual...' showIcon />
            }
            <div
                style={{
                    maxWidth: "35rem",
                    minWidth: "35rem"
                }}
            >
                {formState.map((aspect, aspectIndex) => (
                    <AspectComp
                        key={aspectIndex}
                        aspect={aspect}
                        aspectIndex={aspectIndex}
                        handleMessageChange={handleMessageChange}
                        handlePictogramChange={handlePictogramChange}
                        handleTextAlignChange={handleTextAlignChange}
                    />
                ))}
            </div>
            {/* Vista previa */}
            <h3>Vista Previa</h3>
            <div
                className='mb-4'
                style={{
                    maxWidth: "500px",
                    minWidth: "500px"
                }}
            >
                <PreviewComp formState={formState} currentAspectIndex={currentAspectIndex} />
            </div>
            {/* Botones de Acción */}
            <Space
                className='d-flex justify-content-end'
                style={{
                    maxWidth: "500px",
                    minWidth: "500px"
                }}
            >
                <ButtonIcon icon={faPlus} name="Agregar aspecto" variant="outline-primary" size="sm" onClick={handleAddAspect} disabled={formState.length === 3} />
                <ButtonIcon icon={faPaperPlane} name="Enviar a panel(es)" variant="success" size="sm" onClick={handleSendMessage} />
                <ButtonIcon icon={faDatabase} name="Guardar" variant="primary" size="sm" onClick={handleStoreMessage} />
                <ButtonIcon icon={faArrowsRotate} name="Reset" variant="outline-secondary" size="sm" onClick={handleReset} />
            </Space>

        </div>
    )
}

export default AspectsComp